import { GeneralSettingSchema } from '@blocksuite/affine/shared/services';
import { z } from 'zod';

export const BSEditorSettingSchema = GeneralSettingSchema;

export type FontFamily = 'Sans' | 'Serif' | 'Mono' | 'Custom';
export type EdgelessDefaultTheme = 'auto' | 'dark' | 'light' | 'specified';
export const newDocDateTitleFormatOptions = [
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'YYYY-MM-DD',
  'journal',
] as const;
export type NewDocDateTitleFormat =
  (typeof newDocDateTitleFormatOptions)[number];

export const fontStyleOptions = [
  { key: 'Sans', value: 'var(--affine-font-sans-family)' },
  { key: 'Serif', value: 'var(--affine-font-serif-family)' },
  { key: 'Mono', value: 'var(--affine-font-mono-family)' },
  { key: 'Custom', value: 'var(--affine-font-sans-family)' },
] satisfies {
  key: FontFamily;
  value: string;
}[];

const AffineEditorSettingSchema = z.object({
  fontFamily: z.enum(['Sans', 'Serif', 'Mono', 'Custom']).default('Sans'),
  customFontFamily: z.string().default(''),
  fontSize: z.number().min(12).max(24).default(16),
  newDocDefaultMode: z.enum(['edgeless', 'page', 'ask']).default('page'),
  autoTitleNewDocWithCurrentDate: z.boolean().default(false),
  newDocDateTitleFormat: z
    .enum(newDocDateTitleFormatOptions)
    .default('DD-MM-YYYY'),
  displayAddIconOption: z.boolean().default(true),
  /*
   * ⚠️ **`true` é NOSSO; o upstream traz `false`** (card 1851768965949294521, diretriz do
   * operador: *"a visao full width de docs tb precisa ser padrao sempre"*).
   *
   * Este `.default()` é o ÚNICO lugar onde o valor nasce: `watchAll` (`entities/
   * editor-setting.ts:70`) faz `schema.parse(undefined)` quando o usuário nunca tocou no
   * ajuste. Mudá-lo aqui vale para todo usuário novo de todo ambiente, sem migração de dado
   * e sem tocar em quem já escolheu — a preferência de quem escolheu continua sendo dela,
   * pela mesma razão da Regra 8.1.1 (senha de quem já existe não se sobrescreve).
   *
   * E é o único ponto possível: o ajuste é **por usuário**, guardado no userspace dele
   * (tabela `user_snapshots`), e `core/sync/gateway.ts:988` recusa escrita de userspace que
   * não seja do próprio dono — o provisionamento não alcança. Não existe config de servidor
   * para isto; `fullWidthLayout` não aparece em `packages/backend/` em lugar nenhum.
   */
  fullWidthLayout: z.boolean().default(true),
  displayDocInfo: z.boolean().default(true),
  displayBiDirectionalLink: z.boolean().default(true),
  edgelessDefaultTheme: z
    .enum(['specified', 'dark', 'light', 'auto'])
    .default('specified'),
  openDocMode: z
    .enum([
      'open-in-active-view',
      'open-in-new-view',
      'open-in-new-tab',
      'open-in-center-peek',
    ])
    .default('open-in-active-view'),
  // linux only:
  enableMiddleClickPaste: z.boolean().default(false),
  codeBlockLineNumbers: z.boolean().default(true),
});

export const EditorSettingSchema = BSEditorSettingSchema.merge(
  AffineEditorSettingSchema
);

// oxlint-disable-next-line no-redeclare
export type EditorSettingSchema = z.infer<typeof EditorSettingSchema>;
